import { ArrowCounterClockwise, CaretRight, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { inputCls, labelCls } from "@/lib/ui";
import { VoiceTools } from "@/components/admin/VoiceTools";

// Settings grouped by job. Each section leads with the one choice people actually make (a dropdown),
// shows whether the service it needs has a key, and keeps raw model IDs under "Advanced".
const SECTIONS = [
  {
    title: "Transcription",
    hint: "Turns meeting audio into text. The default engine can also be switched during a meeting from its top bar.",
    main: [
      ["transcription_engine", "Default engine", { options: "engines" }],
      ["transcription_fallback", "If a chunk fails", { options: "fallback" }],
      ["final_speaker_pass", "After the meeting, recheck voices", { options: [["on", "On: one pass over the whole meeting (Scribe, about $0.27 an hour)"], ["off", "Off: keep the live labels"]] }],
    ],
    advanced: [
      ["transcription_model", "Gemini Flash model"],
      ["scribe_model", "ElevenLabs Scribe model"],
      ["speaker_naming_model", "Speaker naming model (Scribe; Gemini, needs your own Gemini key, else Anthropic flag model)"],
      ["transcribe_35_model", "Gemini 3.5 Transcribe model"],
      ["sarvam_stt_model", "Sarvam fallback model"],
      ["sarvam_stt_mode", "Sarvam mode (codemix · transcribe · verbatim)"],
      ["sarvam_stt_language", "Sarvam language (unknown = auto)"],
    ],
  },
  {
    title: "Answers",
    hint: "What runs when someone presses Ask or pulls a flag. The provider is chosen per meeting.",
    main: [
      ["reasoning_effort", "Default thinking effort", { options: [["default", "Model default (fastest)"], ["low", "Low"], ["medium", "Medium"], ["high", "High (slower, costlier)"]] }],
      ["usefulness_threshold", "Show answers scoring at least (1–5)", { type: "number" }],
      ["ask_recent_minutes", "Read the last N minutes word for word (earlier: chapter notes)", { type: "number" }],
    ],
    advanced: [
      ["anthropic_model", "Anthropic model"],
      ["fireworks_reasoning_model", "Fireworks model"],
    ],
  },
  {
    title: "Background flags",
    hint: "The quiet check that puts a red dot beside Ask.",
    main: [
      ["default_flag_interval", "Check every (minutes)", { type: "number" }],
      ["flag_confidence_threshold", "Show a flag at confidence ≥ (0–1)", { type: "number" }],
    ],
    advanced: [
      ["fireworks_flag_model", "Fireworks flag model"],
      ["flag_fallback_model", "Anthropic fallback model"],
    ],
  },
  {
    title: "Read aloud",
    hint: "The speaker button on answer cards. Firmness and pace are set here; how deep a voice sounds comes from which voice is chosen.",
    main: [
      ["tts_provider", "Default voice", { options: "voices" }],
      ["elevenlabs_speed", "ElevenLabs pace (0.7–1.2, 1 = normal)", { type: "number" }],
      ["elevenlabs_stability", "ElevenLabs steadiness (0–1, higher = firmer and flatter)", { type: "number" }],
      ["sarvam_pace", "Sarvam pace (0.5–2, 1 = normal)", { type: "number" }],
    ],
    advanced: [
      ["elevenlabs_voice_id", "ElevenLabs voice ID"],
      ["elevenlabs_similarity", "ElevenLabs similarity to the voice (0–1)", { type: "number" }],
      ["elevenlabs_model", "ElevenLabs model"],
      ["sarvam_speaker", "Sarvam speaker (rahul · sumit · vijay · ratan are the deep ones)"],
      ["sarvam_temperature", "Sarvam steadiness (0.01–1, lower = steadier)", { type: "number" }],
      ["sarvam_tts_model", "Sarvam model"],
      ["sarvam_language", "Sarvam language (en-IN · hi-IN …)"],
    ],
    extra: "voice",
  },
  {
    title: "Chapters",
    hint: "Short notes written every few minutes, so a long meeting stays easy to follow. Ask reads them for the earlier part of the meeting. Each chapter also updates the discussion board (anchor, facts, assumptions, constraints, options, agreed, open). Written by the background-flag models (Fireworks, then Anthropic).",
    main: [["chapter_minutes", "New chapter every (minutes)", { type: "number" }]],
    advanced: [],
  },
  {
    title: "Timing",
    hint: "Shorter chunks put text on screen sooner but mean more transcription calls.",
    main: [
      ["chunk_seconds", "Audio chunk length (seconds)", { type: "number" }],
      ["min_transcript_seconds", "Ask unlocks after (seconds of speech)", { type: "number" }],
      ["private_idle_minutes", "Wipe a private meeting nobody has opened for (minutes)", { type: "number" }],
    ],
    advanced: [],
  },
];

const Status = ({ ok, label }) => (
  <span className={`inline-flex items-center gap-1 font-mono text-[10px] ${ok ? "text-[#10B981]" : "text-[#F59E0B]"}`}>
    {ok ? <CheckCircle size={12} weight="fill" /> : <WarningCircle size={12} weight="fill" />} {label}
  </span>
);

const optionsFor = (spec, cfg) => {
  if (spec === "engines") {
    return Object.entries(cfg.engines || {}).map(([k, e]) => [k, `${e.label}${e.configured ? "" : " · needs key"}`, e]);
  }
  if (spec === "voices") {
    return Object.entries(cfg.voices || {}).map(([k, v]) => [k, `${v.label}${v.configured ? "" : " · needs key"}`, v]);
  }
  if (spec === "fallback") {
    const sarvam = { configured: !!cfg.voices?.sarvam?.configured, note: "Fills the gap with text, without speaker names." };
    return [["sarvam", `Retry with Sarvam${sarvam.configured ? "" : " · needs key"}`, sarvam], ["none", "Leave a gap"]];
  }
  return spec;
};

const Field = ({ k, label, opts = {}, draft, setDraft, defaults, onReset, cfg }) => {
  const changed = String(draft[k]) !== String(defaults[k]);
  const options = opts.options ? optionsFor(opts.options, cfg) : null;
  const picked = options?.find(([v]) => v === draft[k])?.[2];
  return (
    <label className="space-y-2 block">
      <span className="flex items-center justify-between gap-2">
        <span className={labelCls}>{label}</span>
        {changed && (
          <button type="button" onClick={() => onReset(k)} className="text-[var(--o-muted3)] hover:text-[var(--o-fg)] transition-colors" title={`Reset to ${defaults[k]}`} data-testid={`setting-reset-${k}`}>
            <ArrowCounterClockwise size={12} />
          </button>
        )}
      </span>
      {options ? (
        <select className={`${inputCls} font-mono text-xs`} value={draft[k] ?? ""} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })} data-testid={`setting-${k}`}>
          {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      ) : (
        <input type={opts.type || "text"} step="any" className={`${inputCls} font-mono text-xs`} value={draft[k] ?? ""} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })} data-testid={`setting-${k}`} />
      )}
      {picked && (
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-[var(--o-muted2)]">
          <Status ok={picked.configured} label={picked.configured ? "key set" : "add its key above"} />
          {picked.note}
          {picked.model && <span className="font-mono text-[var(--o-muted3)]">{picked.model}</span>}
        </span>
      )}
    </label>
  );
};

export const SettingsPanel = ({ draft, setDraft, defaults, onReset, cfg = {} }) => {
  const field = (k, label, opts) => (
    <Field key={k} k={k} label={label} opts={opts} draft={draft} setDraft={setDraft} defaults={defaults} onReset={onReset} cfg={cfg} />
  );
  return (
    <div className="space-y-6" data-testid="settings-panel">
      {SECTIONS.map((sec) => (
        <div key={sec.title} className="border border-[var(--o-border)]" data-testid={`settings-section-${sec.title.toLowerCase().replace(/\s+/g, "-")}`}>
          <div className="px-5 py-4 border-b border-[var(--o-border)]">
            <h2 className="font-display text-base md:text-lg font-bold">{sec.title}</h2>
            <p className="text-xs text-[var(--o-muted2)] mt-1">{sec.hint}</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-5 p-5">{sec.main.map(([k, l, o]) => field(k, l, o))}</div>
          {sec.extra === "voice" && <VoiceTools draft={draft} setDraft={setDraft} cfg={cfg} />}
          {sec.advanced.length > 0 && (
            <details className="group border-t border-[var(--o-border-soft)]">
              <summary className="cursor-pointer list-none px-5 py-3 flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.14em] text-[var(--o-muted2)] hover:text-[var(--o-fg)]" data-testid={`settings-advanced-${sec.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <CaretRight size={11} className="transition-transform group-open:rotate-90" /> Advanced · model IDs
              </summary>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-4 px-5 pb-5">{sec.advanced.map(([k, l, o]) => field(k, l, o))}</div>
            </details>
          )}
        </div>
      ))}
      <div className="border border-[var(--o-border)]">
        <div className="px-5 py-4 border-b border-[var(--o-border)]">
          <h2 className="font-display text-base md:text-lg font-bold">Prompts</h2>
          <p className="text-xs text-[var(--o-muted2)] mt-1">Apply to new sessions.</p>
        </div>
        <div className="p-5 space-y-5">
          {[["system_prompt", "Observer brief (system prompt)"], ["flag_prompt", "Flag check prompt"]].map(([k, label]) => (
            <label key={k} className="space-y-2 block">
              <span className="flex items-center justify-between">
                <span className={labelCls}>{label}</span>
                {draft[k] !== defaults[k] && (
                  <button type="button" onClick={() => onReset(k)} className="flex items-center gap-1 font-mono text-[10px] text-[var(--o-muted2)] hover:text-[var(--o-fg)]" data-testid={`setting-reset-${k}`}>
                    <ArrowCounterClockwise size={11} /> default
                  </button>
                )}
              </span>
              <textarea className={`${inputCls} font-mono text-[11px] leading-relaxed min-h-[260px]`} value={draft[k] ?? ""} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })} spellCheck={false} data-testid={`setting-${k}`} />
            </label>
          ))}
        </div>
      </div>
    </div>
  );
};
